import { useMutation } from '@apollo/client';
import {
    RECORD_SEARCH_TERM,
    RecordSearchTermMutation,
    RecordSearchTermMutationVariables,
    SearchTermDomain,
} from '@deltasierra/frontend-graphql-core';
import { captureException } from '@sentry/nextjs';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

export type RecordSearchTermOptions = {
    clientId: string;
    domain: SearchTermDomain;
    // Null when the search experience has no location, such as the client level template builders
    locationId: string | null;
    resultCount: number | null;
    searchTerm: string;
};

export type RecordSearchTerm = (options: RecordSearchTermOptions) => void;

/**
 * Records a submitted free text search term for search term reporting.
 *
 * The returned function is fire and forget so that recording never blocks or breaks the search experience.
 * Failures are reported to Sentry instead of being surfaced to the user.
 * Empty and whitespace only search terms are not recorded.
 */
export function useRecordSearchTerm(): RecordSearchTerm {
    const [recordSearchTermMutation] = useMutation<RecordSearchTermMutation, RecordSearchTermMutationVariables>(
        RECORD_SEARCH_TERM,
    );

    return useCallback(
        ({ clientId, domain, locationId, resultCount, searchTerm }: RecordSearchTermOptions) => {
            if (searchTerm.trim() === '') {
                return;
            }

            void recordSearchTermMutation({
                variables: {
                    input: {
                        browser: window.navigator.userAgent,
                        clientId,
                        domain,
                        locationId,
                        resultCount,
                        searchTerm,
                        timestamp: DateTime.utc().toString() as DateTimeString,
                        timezone: DateTime.now().zoneName ?? '',
                    },
                },
            })
                .then(({ data }) => {
                    if (data?.recordSearchTerm.__typename === 'RecordSearchTermError') {
                        const { __typename: type, code, message } = data.recordSearchTerm;
                        captureException(new Error(`${code}: ${type} ${message}`));
                    }
                })
                .catch(captureException);
        },
        [recordSearchTermMutation],
    );
}
